<script setup lang="ts">
import { computed } from 'vue'
import { toHtmlDocument } from '@/core/render'

const props = defineProps<{ html: string; title: string }>()

/**
 * The HTML preview renders inside a sandboxed iframe rather than inline.
 *
 * The markup is already sanitised, so this is defence in depth — but it also
 * buys correctness: a converted document's own styles and layout render in a
 * clean context instead of inheriting the app's stylesheet, which is what
 * "preview the HTML file" is supposed to show. `allow-same-origin` is
 * deliberately omitted, so the frame cannot reach back into the page.
 */
const document = computed(() =>
  toHtmlDocument(
    `<style>
      :root { color-scheme: light dark; }
      body {
        margin: 0; padding: 1.5rem; line-height: 1.7;
        font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif;
      }
      img { max-width: 100%; height: auto; }
      table { border-collapse: collapse; }
      th, td { border: 1px solid #cbd5e1; padding: 0.45em 0.75em; text-align: left; }
      pre { background: #f1f5f9; padding: 1em; border-radius: 0.5rem; overflow-x: auto; }
      code { font-family: ui-monospace, monospace; font-size: 0.875em; }
    </style>
    ${props.html}`,
    props.title,
  ),
)
</script>

<template>
  <iframe
    :srcdoc="document"
    sandbox=""
    class="h-full w-full border-0 bg-white"
    :title="`HTML preview of ${title}`"
  />
</template>
